import { deleteCourt, getClubById } from '@/api/clubs/clubApi';
import { CLUB_FIELDS } from '@/api/clubs/clubs-fields.config';
import { DeleteCourtConfirmModal } from '@/components/clubs';
import {
  surfaceMap,
  wallTypeMap,
} from '@/components/clubs/upsert-court/validations';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { Court } from '@/types/club.model';
import { handleGlobalError } from '@/utils/error-handler';
import {
  ChevronLeft,
  Crown,
  Eye,
  LoaderCircle,
  Plus,
  PlusCircle,
  SquarePen,
  Trash2,
  UserCheck,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const Courts: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const clubId = params.id || location.state?.clubId;

  const isReadOnly =
    new URLSearchParams(location.search).get('readonly') === 'true' ||
    location.state?.isReadOnly === true;

  const [courts, setCourts] = useState<Court[]>([]);
  const [clubName, setClubName] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedCourt, setSelectedCourt] = useState<Court | null>(null);

  useEffect(() => {
    const loadCourts = async () => {
      if (!clubId) {
        navigate('/clubs');
        return;
      }

      try {
        setLoading(true);
        const clubData = await getClubById(clubId, CLUB_FIELDS.FULL);
        setCourts(clubData.courts || []);
        setClubName(clubData.name);
      } catch (error) {
        console.error('Error loading courts:', error);
        handleGlobalError(
          error,
          'Error',
          'No se pudieron cargar las canchas. Por favor, intente nuevamente.',
        );
        if (location.state?.courts) {
          setCourts(location.state.courts);
        }
      } finally {
        setLoading(false);
      }
    };

    loadCourts();
  }, [clubId, navigate, location.state?.courts]);

  const handleNavigateToCreateCourt = () => {
    navigate(`/clubs/${clubId}/courts/create`, {
      state: { clubId },
    });
  };

  const updateCourt = (court: Court) => {
    navigate(`/clubs/${clubId}/courts/create`, {
      state: {
        clubId,
        court,
        isReadOnly,
      },
    });
  };

  const openDialogDeleteCourt = (court: Court) => {
    if (isReadOnly) return;
    setShowDeleteModal(true);
    setSelectedCourt(court);
  };

  const handleOnOpenChange = () => {
    setShowDeleteModal(false);
    setIsDeleting(false);
  };

  const handleDeleteCourt = async () => {
    if (!selectedCourt?.id) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudo identificar la cancha a eliminar.',
      });
      return;
    }

    try {
      setIsDeleting(true);
      await deleteCourt(selectedCourt.id);
      setCourts(courts.filter((court) => court.id !== selectedCourt?.id));
      setSelectedCourt(null);
      setShowDeleteModal(false);
      toast({
        variant: 'default',
        title: 'Eliminación',
        description: 'La cancha fue eliminada correctamente',
      });
    } catch (error) {
      handleGlobalError(
        error,
        'Error',
        'No se puedo eliminar la cancha. Por favor, intente nuevamente más tarde.',
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="mb-8">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate('/clubs')}
            className="flex items-center gap-2"
          >
            <ChevronLeft size={16} />
          </Button>
          {isReadOnly ? (
            <UserCheck size={28} className="text-blue-600" />
          ) : (
            <Crown size={28} className="text-yellow-600" />
          )}
          <div>
            <h2 className="text-2xl font-bold">{`Canchas: ${clubName}`}</h2>
            {isReadOnly && (
              <p className="mt-1 text-sm text-gray-600">
                Tienes permisos de manager en este club
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isReadOnly && (
            <Button
              className="flex items-center gap-2"
              onClick={handleNavigateToCreateCourt}
            >
              <Plus size={18} />
              <span>Nueva cancha</span>
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <LoaderCircle className="mx-auto h-8 w-8 animate-spin text-blue-600" />
            <p className="mt-2 text-sm text-gray-600">Cargando canchas...</p>
          </div>
        </div>
      ) : courts.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-4">
          {courts.map((court) => (
            <div
              key={court.id}
              className={`relative rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 ${
                isReadOnly ? 'border-blue-200 bg-blue-50/30' : ''
              }`}
            >
              <h2
                className={`mb-4 flex items-center justify-between rounded-tl-lg rounded-tr-lg p-4 text-xl capitalize text-white ${
                  isReadOnly ? 'bg-blue-600/90' : 'bg-primary/90'
                }`}
              >
                <div className="flex items-center gap-2">
                  {isReadOnly && <Eye size={18} />}
                  {court.name}
                </div>
              </h2>

              <div className="flex flex-col gap-2 px-4 pb-4">
                <div className="flex justify-between">
                  <span className="font-bold underline">Deporte:</span>
                  <span>{court.sport.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-bold underline">Superficie:</span>
                  <span>{surfaceMap[court.surface]}</span>
                </div>
                {court.wallType && (
                  <div className="flex justify-between">
                    <span className="font-bold underline">Pared:</span>
                    <span>{wallTypeMap[court.wallType]}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-bold underline">Cubierta:</span>
                  <span>{court.hasRoof ? 'Sí' : 'No'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-bold underline">Precio:</span>
                  <span>${court.price.value}</span>
                </div>
                {!isReadOnly && (
                  <div className="mt-4 flex flex-col gap-3 pb-4 pt-0">
                    <Button
                      className="flex w-full gap-1"
                      variant="outline"
                      onClick={() => updateCourt(court)}
                    >
                      <SquarePen size={'14'} />
                      <span>Editar cancha</span>
                    </Button>
                    <Button
                      variant={'outline'}
                      className="flex w-full gap-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDialogDeleteCourt(court);
                      }}
                    >
                      <Trash2 size={'14'} className="text-red-500" />
                      <span className="text-red-500">Eliminar cancha</span>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {!isReadOnly && (
            <DeleteCourtConfirmModal
              isOpen={showDeleteModal}
              onOpenChange={handleOnOpenChange}
              isLoading={isDeleting}
              onDeleteCourt={handleDeleteCourt}
            />
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mb-4 text-gray-400">
            {isReadOnly ? <Eye size={48} /> : <PlusCircle size={48} />}
          </div>
          <h3 className="mb-2 text-lg font-semibold text-gray-600">
            {isReadOnly
              ? 'Este club no tiene canchas'
              : 'Aún no posee canchas creadas para este club'}
          </h3>
          <p className="text-sm text-gray-500">
            {isReadOnly
              ? 'No hay canchas disponibles para mostrar en este club.'
              : 'Agrega tu primera cancha para comenzar a gestionar las instalaciones.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default Courts;
